import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Button, Typeahead } from '@src/components/ui';
import { FormField, StateZipRow, RadioGroup, CorporationTypeahead } from './components';
import { useCorporateGroups, useFuzzySearch } from './hooks';
import { formatPhoneNumber, extractPhoneNumber, extractAddressData, transformApiData, } from './utils';
import { BUYING_GROUP_CHOICES } from './constants';
import styles from './ClinicForm.module.scss';
const INITIAL_FORM_DATA = {
    name: '',
    street_1: '',
    street_2: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    alternate_phone: '',
    email: '',
    fax: '',
    billing_street_1: '',
    billing_street_2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    is_corporation_owned: '',
    corporation_name: '',
    custom_corporation_name: '',
    buying_group: '',
    other_buying_group: '',
};
export const ClinicForm = ({ mode, clinic, onSubmit, onCancel, showBillingAddress: showBillingAddressProp = false, showTermsAndConditions = false, }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showBillingAddress, setShowBillingAddress] = useState(showBillingAddressProp);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [corporationSearchValue, setCorporationSearchValue] = useState('');
    const [buyingGroupInput, setBuyingGroupInput] = useState('');
    const { corporateGroups, loading: corporateGroupsLoading } = useCorporateGroups(true);
    const filteredCorporations = useFuzzySearch(corporateGroups, corporationSearchValue);
    useEffect(() => {
        if (mode === 'edit' && clinic) {
            const addressData = extractAddressData(clinic.address);
            const billingAddressData = clinic.billing_address
                ? extractAddressData(clinic.billing_address)
                : { street_1: '', street_2: '', city: '', state: '', postal_code: '' };
            setFormData({
                name: clinic.name || clinic.clinic_name || '',
                ...addressData,
                phone: formatPhoneNumber(extractPhoneNumber(clinic.phone)),
                alternate_phone: formatPhoneNumber(extractPhoneNumber(clinic.alternate_phone)),
                email: clinic.email || '',
                fax: formatPhoneNumber(extractPhoneNumber(clinic.fax)),
                billing_street_1: billingAddressData.street_1 || '',
                billing_street_2: billingAddressData.street_2 || '',
                billing_city: billingAddressData.city || '',
                billing_state: billingAddressData.state || '',
                billing_postal_code: billingAddressData.postal_code || '',
                is_corporation_owned: clinic.is_corporation_owned ? 'yes' : 'no',
                corporation_name: clinic.corporation_name || '',
                custom_corporation_name: clinic.custom_corporation_name || '',
                buying_group: clinic.buying_group || '',
                other_buying_group: clinic.other_buying_group || '',
            });
            if (clinic.buying_group) {
                const buyingGroup = BUYING_GROUP_CHOICES.find((bg) => bg.value === clinic.buying_group);
                setBuyingGroupInput(buyingGroup ? buyingGroup.label : clinic.buying_group);
            }
            if (clinic.billing_address) {
                setShowBillingAddress(true);
            }
        }
    }, [mode, clinic]);
    const corporationSuggestions = useMemo(() => {
        if (corporateGroupsLoading) {
            return [{ id: 'loading', label: 'Loading...', value: '', disabled: true }];
        }
        const suggestions = filteredCorporations.map((corp) => ({
            id: `corp-${corp.id}`,
            label: corp.abbreviation ? `${corp.name} (${corp.abbreviation})` : corp.name,
            value: corp.name,
        }));
        suggestions.push({ id: 'corp-other', label: 'Other', value: 'Other' });
        return suggestions;
    }, [filteredCorporations, corporateGroupsLoading]);
    const validateForm = () => {
        const errors = {};
        const requiredFields = mode === 'create'
            ? ['name', 'street_1', 'city', 'state', 'postal_code', 'phone', 'email']
            : ['name', 'email'];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                errors[field] = 'This field is required';
            }
        });
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (formData.phone) {
            const phoneDigits = formData.phone.replace(/\D/g, '');
            if (phoneDigits.length !== 10) {
                errors.phone = 'Phone number must be 10 digits';
            }
        }
        if (formData.postal_code && !/^\d{5}(-\d{4})?$/.test(formData.postal_code)) {
            errors.postal_code = 'Please enter a valid ZIP code';
        }
        if (mode === 'create' && !formData.is_corporation_owned) {
            errors.is_corporation_owned = 'Please select an option';
        }
        if (formData.is_corporation_owned === 'yes') {
            if (!formData.corporation_name) {
                errors.corporation_name = 'Please select a corporation';
            }
            if (formData.corporation_name === 'Other' && !formData.custom_corporation_name) {
                errors.custom_corporation_name = 'Please enter the corporation name';
            }
        }
        if (showBillingAddress) {
            const billingRequiredFields = [
                'billing_street_1',
                'billing_city',
                'billing_state',
                'billing_postal_code',
            ];
            billingRequiredFields.forEach((field) => {
                if (!formData[field]) {
                    errors[field] = 'This field is required';
                }
            });
            if (formData.billing_postal_code && !/^\d{5}(-\d{4})?$/.test(formData.billing_postal_code)) {
                errors.billing_postal_code = 'Please enter a valid ZIP code';
            }
        }
        if (mode === 'create' && showTermsAndConditions && !termsAccepted) {
            errors.terms = 'You must accept the Terms of Service and Return/Refund Policy';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleInputChange = (field) => (value) => {
        const processedValue = field === 'phone' || field === 'fax' ? formatPhoneNumber(value) : value;
        setFormData((prev) => ({ ...prev, [field]: processedValue }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };
    const handleCorporationSelect = (suggestion) => {
        setFormData((prev) => ({
            ...prev,
            corporation_name: suggestion.value,
            custom_corporation_name: suggestion.value !== 'Other' ? '' : prev.custom_corporation_name,
        }));
        if (fieldErrors.corporation_name) {
            setFieldErrors((prev) => ({ ...prev, corporation_name: '' }));
        }
    };
    const handleCorporationChange = (e) => {
        const value = e.target.value || '';
        setCorporationSearchValue(value);
        setFormData((prev) => ({
            ...prev,
            corporation_name: value,
            custom_corporation_name: value === '' ? '' : prev.custom_corporation_name,
        }));
        if (fieldErrors.corporation_name) {
            setFieldErrors((prev) => ({ ...prev, corporation_name: '' }));
        }
    };
    const handleCorporationSearch = (value) => {
        setCorporationSearchValue(value || '');
        setFormData((prev) => ({
            ...prev,
            corporation_name: value || '',
            custom_corporation_name: value === '' ? '' : prev.custom_corporation_name,
        }));
    };
    const handleBuyingGroupChange = (input) => {
        const value = typeof input === 'string' ? input : (input?.target?.value ?? '');
        setBuyingGroupInput(value);
        setFormData((prev) => ({ ...prev, buying_group: '' }));
        if (fieldErrors.buying_group) {
            setFieldErrors((prev) => ({ ...prev, buying_group: '' }));
        }
    };
    const handleBuyingGroupSelect = (selected) => {
        const isOther = selected?.value === 'Other';
        setFormData((prev) => ({
            ...prev,
            buying_group: isOther ? '' : selected?.value || '',
            other_buying_group: isOther ? prev.other_buying_group : '',
        }));
        setBuyingGroupInput(selected?.label || '');
        if (fieldErrors.buying_group) {
            setFieldErrors((prev) => ({ ...prev, buying_group: '' }));
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        try {
            const dataToSubmit = mode === 'edit'
                ? transformApiData(formData, clinic)
                : {
                    ...formData,
                    clinic_name: formData.name,
                    buying_groups: formData.buying_group ? [formData.buying_group] : [],
                    phone: formData.phone ? formData.phone.replace(/\D/g, '') : '',
                    alternate_phone: formData.alternate_phone
                        ? formData.alternate_phone.replace(/\D/g, '')
                        : '',
                    fax: formData.fax ? formData.fax.replace(/\D/g, '') : '',
                };
            await onSubmit(dataToSubmit);
        }
        catch (err) {
            if (err.response?.body) {
                const apiErrors = err.response.body;
                const newFieldErrors = {};
                Object.entries(apiErrors).forEach(([key, value]) => {
                    if (typeof value === 'object' && !Array.isArray(value)) {
                        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                            if (Array.isArray(nestedValue) && nestedValue.length > 0) {
                                if (key === 'address') {
                                    if (nestedKey === 'street1')
                                        newFieldErrors['street_1'] = nestedValue[0];
                                    if (nestedKey === 'postalCode')
                                        newFieldErrors['postal_code'] = nestedValue[0];
                                    if (nestedKey === 'city')
                                        newFieldErrors['city'] = nestedValue[0];
                                    if (nestedKey === 'state')
                                        newFieldErrors['state'] = nestedValue[0];
                                }
                            }
                        });
                    }
                    else if (Array.isArray(value) && value.length > 0) {
                        newFieldErrors[key] = value[0];
                    }
                });
                setFieldErrors(newFieldErrors);
                setError('Please fix the errors below');
            }
            else {
                setError(err.message || `Failed to ${mode} clinic`);
            }
            setLoading(false);
        }
    };
    const getFieldLabel = (label, field) => {
        return fieldErrors[field] ? `${label}: ${fieldErrors[field]}` : label;
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: styles.clinicForm, children: [mode === 'create' && (_jsxs("div", { className: styles.headingContainer, children: [_jsx("h3", { children: "Set Up My Clinic" }), _jsx("p", { children: "Fill out the form below and we'll manually validate the information and get back to you with approval within 24 hours." })] })), _jsxs("div", { className: styles.inputContainer, children: [_jsx(FormField, { label: getFieldLabel('Clinic Name', 'name'), value: formData.name, onChange: handleInputChange('name'), error: !!fieldErrors.name, className: styles.inputItem }), _jsx(FormField, { label: getFieldLabel('Clinic Street Address 01', 'street_1'), value: formData.street_1, onChange: handleInputChange('street_1'), error: !!fieldErrors.street_1, className: styles.inputItem }), _jsx(FormField, { label: "Clinic Street Address 02 (optional)", value: formData.street_2, onChange: handleInputChange('street_2'), className: styles.inputItem }), _jsx(FormField, { label: getFieldLabel('City', 'city'), value: formData.city, onChange: handleInputChange('city'), error: !!fieldErrors.city, className: styles.inputItem }), _jsx(StateZipRow, { stateValue: formData.state, zipValue: formData.postal_code, onStateChange: handleInputChange('state'), onZipChange: handleInputChange('postal_code'), stateError: !!fieldErrors.state, zipError: !!fieldErrors.postal_code }), _jsx(FormField, { label: getFieldLabel('Clinic Phone Number', 'phone'), value: formData.phone, onChange: handleInputChange('phone'), error: !!fieldErrors.phone, className: styles.inputItem }), _jsx(FormField, { label: "Clinic Alternate Phone Number (optional)", value: formData.alternate_phone, onChange: handleInputChange('alternate_phone'), className: styles.inputItem }), _jsx(FormField, { label: getFieldLabel('Clinic Email Address', 'email'), value: formData.email, onChange: handleInputChange('email'), error: !!fieldErrors.email, className: styles.inputItem }), _jsx(FormField, { label: "Clinic Fax Number (optional)", value: formData.fax, onChange: handleInputChange('fax'), className: styles.inputItem }), mode === 'create' && (_jsx("div", { className: styles.billingSection, children: _jsxs("label", { className: styles.billingToggle, children: [_jsx("input", { type: "checkbox", checked: showBillingAddress, onChange: (e) => setShowBillingAddress(e.target.checked) }), _jsx("span", { children: "Add billing address if different from above" })] }) })), showBillingAddress && (_jsxs("div", { className: styles.billingAddressContainer, children: [_jsx("h4", { children: "Billing Address" }), _jsx(FormField, { label: getFieldLabel('Street Address 01', 'billing_street_1'), value: formData.billing_street_1, onChange: handleInputChange('billing_street_1'), error: !!fieldErrors.billing_street_1, className: styles.inputItem }), _jsx(FormField, { label: "Street Address 02 (optional)", value: formData.billing_street_2, onChange: handleInputChange('billing_street_2'), className: styles.inputItem }), _jsx(FormField, { label: getFieldLabel('City', 'billing_city'), value: formData.billing_city, onChange: handleInputChange('billing_city'), error: !!fieldErrors.billing_city, className: styles.inputItem }), _jsx(StateZipRow, { stateValue: formData.billing_state, zipValue: formData.billing_postal_code, onStateChange: handleInputChange('billing_state'), onZipChange: handleInputChange('billing_postal_code'), stateError: !!fieldErrors.billing_state, zipError: !!fieldErrors.billing_postal_code })] })), _jsx(RadioGroup, { label: "Is your clinic owned by a corporation?", name: "is_corporation_owned", value: formData.is_corporation_owned, options: [
                            { value: 'no', label: 'No' },
                            { value: 'yes', label: 'Yes' },
                        ], onChange: (value) => setFormData((prev) => ({ ...prev, is_corporation_owned: value })), error: !!fieldErrors.is_corporation_owned, className: styles.corporationSection }), formData.is_corporation_owned === 'yes' && (_jsxs("div", { className: styles.corporationOptions, children: [_jsx(CorporationTypeahead, { value: formData.corporation_name, suggestions: corporationSuggestions, onSelect: handleCorporationSelect, onChange: handleCorporationChange, onSearch: handleCorporationSearch, loading: corporateGroupsLoading, error: !!fieldErrors.corporation_name, className: styles.inputItem }), formData.corporation_name === 'Other' && (_jsx(FormField, { label: getFieldLabel('Corporation Name', 'custom_corporation_name'), value: formData.custom_corporation_name, onChange: handleInputChange('custom_corporation_name'), error: !!fieldErrors.custom_corporation_name, placeholder: "Enter corporation name", className: styles.inputItem }))] })), mode === 'create' && (_jsxs("div", { className: styles.buyingGroupsSection, children: [_jsx(Typeahead, { label: getFieldLabel('Do you belong to any buying groups?', 'buying_group'), placeholder: "Search for buying groups (PSI, Vertical Vet, VMG, TVC)...", suggestions: BUYING_GROUP_CHOICES, value: buyingGroupInput, onChange: handleBuyingGroupChange, onSelect: handleBuyingGroupSelect, clearable: true, invalid: !!fieldErrors.buying_group, className: styles.inputItem, inputWidth: "100%", showTopOverride: false, valueTriggerLimit: 1 }), buyingGroupInput === 'Other' && (_jsx(FormField, { label: getFieldLabel('Please specify the buying group', 'other_buying_group'), value: formData.other_buying_group, onChange: handleInputChange('other_buying_group'), error: !!fieldErrors.other_buying_group, placeholder: "Enter buying group name", className: styles.inputItem }))] })), mode === 'create' && showTermsAndConditions && (_jsxs("div", { className: styles.termsSection, children: [_jsxs("label", { className: styles.checkboxContainer, children: [_jsx("input", { type: "checkbox", checked: termsAccepted, onChange: (e) => setTermsAccepted(e.target.checked) }), _jsxs("span", { className: styles.checkboxText, children: ["I have read and I accept Endurance Technology's", ' ', _jsx("a", { href: "/terms-of-service", target: "_blank", rel: "noopener noreferrer", children: "Terms of Service" }), ' ', "and", ' ', _jsx("a", { href: "/return-refund-policy", target: "_blank", rel: "noopener noreferrer", children: "Return/Refund Policy" }), "."] })] }), fieldErrors.terms && _jsx("p", { className: styles.error, children: fieldErrors.terms })] })), error && _jsx("p", { className: styles.error, children: error }), _jsxs("div", { className: styles.buttonContainer, children: [mode === 'edit' && onCancel && (_jsx(Button, { variant: "secondary", label: "Cancel", size: "small", type: "button", onClick: onCancel, disabled: loading })), _jsx(Button, { variant: "primaryV2", label: loading
                                    ? mode === 'create'
                                        ? 'Creating Account...'
                                        : 'Saving...'
                                    : mode === 'create'
                                        ? 'Create Account'
                                        : 'Save Changes', size: mode === 'create' ? 'large' : 'small', type: "submit", disabled: loading })] })] })] }));
};
export default ClinicForm;
